import { Box, styled, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

const borderSubtle = (theme) => `1px solid ${theme.palette.custom.lightPink}`;

export const StyledModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 680,
  maxHeight: "84vh",
  overflow: "hidden",
  background: theme.palette.custom.white,
  borderRadius: theme.borderRadius.radius.large,
  outline: "none",
  boxShadow: "0 28px 80px rgba(11, 101, 181, 0.14)",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    width: "85vw",
  },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
  },
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "22px 28px",
  gap: 16,
  borderTopLeftRadius: theme.borderRadius.radius.large,
  borderTopRightRadius: theme.borderRadius.radius.large,
  background: `linear-gradient(135deg, ${theme.palette.custom.primary} 0%, ${theme.palette.custom.orderCards} 100%)`,
  color: theme.palette.custom.white,
  position: "sticky",
  top: 0,
  zIndex: 2,
  boxShadow: "0 8px 24px rgba(11, 37, 115, 0.2)",
  flexShrink: 0,
}));

export const HeaderTitleRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  minWidth: 0,
}));

export const HeaderIconWrap = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.borderRadius.radius.medium,
  background: "rgba(255, 255, 255, 0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: "1px solid rgba(255, 255, 255, 0.25)",
  img: {
    width: 28,
    height: 28,
    objectFit: "contain",
  },
}));

export const StyledContent = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  minHeight: 0,
  flex: 1,
  background: theme.palette.custom.screenBgColor,
  scrollbarGutter: "stable",
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.large.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color: theme.palette.custom.white,
  letterSpacing: "0.01em",
}));

export const ModalDescription = styled(Typography)(({ theme }) => ({
  marginTop: 6,
  color: "rgba(255, 255, 255, 0.88)",
  fontSize: theme.typography.small.fontSize,
  lineHeight: 1.65,
  maxWidth: 420,
}));

export const StyledSection = styled(Box)(({ theme }) => ({
  padding: "22px 28px 24px",
  borderBottom: borderSubtle(theme),
  background: theme.palette.custom.white,
  "&:last-of-type": {
    borderBottom: "none",
  },
}));

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.base.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color: theme.palette.custom.orderName,
  marginBottom: 16,
  paddingLeft: 12,
  borderLeft: `4px solid ${theme.palette.custom.primary}`,
  lineHeight: 1.3,
}));

export const StyledSectionTitleRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 16,
  "& > *:first-of-type": {
    marginBottom: 0,
  },
}));

export const StyledSectionMeta = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  color: theme.palette.custom.notProvide,
  fontWeight: theme.typography.fontWeight.medium,
  flexShrink: 0,
}));

export const StyledSummaryGrid = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
}));

export const StyledSummaryItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "18px 20px",
  borderRadius: theme.borderRadius.radius.medium,
  background: theme.palette.custom.tabBackgroundColor,
  border: borderSubtle(theme),
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.custom.chipsBgColor,
    boxShadow: "0 4px 14px rgba(11, 101, 181, 0.08)",
  },
}));

export const StyledSummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  color: theme.palette.custom.notProvide,
  fontWeight: theme.typography.fontWeight.medium,
  letterSpacing: "0.02em",
}));

export const StyledSummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.base.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color: theme.palette.custom.blackColor,
  wordBreak: "break-word",
}));

export const StyledStatsGrid = styled(Box)(() => ({
  display: "flex",
  gap: 16,
  flexWrap: "wrap",
}));

export const StyledStatCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cardVariant",
})(({ theme, cardVariant = "total" }) => {
  const w = theme.palette.custom.warning;
  const join = theme.palette.custom.chipJoinColor;
  const primary = theme.palette.custom.primary;
  const byVariant = {
    invited: {
      background: alpha(w, 0.07),
      borderColor: alpha(w, 0.22),
    },
    joined: {
      background: alpha(join, 0.07),
      borderColor: alpha(join, 0.2),
    },
    total: {
      background: alpha(primary, 0.06),
      borderColor: alpha(primary, 0.16),
    },
  };
  const v = byVariant[cardVariant] || byVariant.total;
  return {
    flex: 1,
    minWidth: 160,
    padding: "20px 18px",
    borderRadius: theme.borderRadius.radius.medium,
    background: v.background,
    border: `1px solid ${v.borderColor}`,
    boxShadow: "0 4px 16px rgba(11, 37, 115, 0.04)",
    minHeight: 108,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
});

export const StyledStatLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  color: theme.palette.custom.orderButtonColor,
  marginBottom: 8,
  fontWeight: theme.typography.fontWeight.medium,
}));

export const StyledStatValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "valueVariant",
})(({ theme, valueVariant = "total" }) => {
  const w = theme.palette.custom.warning;
  const join = theme.palette.custom.chipJoinColor;
  const primary = theme.palette.custom.primary;
  const colorMap = {
    invited: alpha(w, 0.62),
    joined: alpha(join, 0.58),
    total: alpha(primary, 0.62),
  };
  return {
    fontSize: theme.typography.xxLarge.fontSize,
    fontWeight: theme.typography.fontWeight.semiBold,
    color: colorMap[valueVariant] || colorMap.total,
    lineHeight: 1.15,
  };
});

export const StyledItemList = styled(Box)(() => ({
  display: "grid",
  gap: 10,
}));

export const StyledItemRow = styled(Box)(({ theme }) => ({
  padding: "14px 16px 14px 14px",
  borderRadius: theme.borderRadius.radius.medium,
  background: theme.palette.custom.white,
  border: borderSubtle(theme),
  borderLeft: `4px solid ${theme.palette.custom.primary}`,
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  boxShadow: "0 4px 16px rgba(15, 45, 71, 0.05)",
  transition: "box-shadow 0.2s ease, transform 0.15s ease",
  "&:hover": {
    boxShadow: "0 8px 22px rgba(11, 101, 181, 0.1)",
  },
}));

export const StyledItemIndex = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.mini.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color: theme.palette.custom.primary,
  background: theme.palette.custom.chipsBgColor,
  minWidth: 28,
  height: 28,
  borderRadius: theme.borderRadius.radius.small,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginTop: 2,
}));

export const StyledItemTextBlock = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 0,
  flex: 1,
}));

export const StyledItemName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.medium.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color: theme.palette.custom.blackColor,
  lineHeight: 1.35,
}));

export const StyledItemMeta = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  color: theme.palette.custom.orderButtonColor,
  lineHeight: 1.45,
}));

export const StyledNoRecords = styled(Box)(({ theme }) => ({
  padding: "28px 20px",
  textAlign: "center",
  borderRadius: theme.borderRadius.radius.medium,
  background: theme.palette.custom.white,
  border: `1px dashed ${theme.palette.custom.lightPink}`,
}));

export const StyledNoRecordsText = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.notProvide,
  fontSize: theme.typography.small.fontSize,
  lineHeight: 1.6,
}));
